import React from "react";
import { PanelShell, type PanelMeta } from "./PanelShell";

type GazetteNotice = {
  Hash_Key: string;
  Notice_Type: string;
  Notice_Code: string;
  Gazette_Date: string;
  Last_Modified_Date?: string | null;
  Company_Name?: string | null;
  Company_Number?: string | null;
  Record_Type?: number | null;
  Sequence_Number?: number | null;
};

type GazetteResponse = {
  notices: GazetteNotice[];
  summary: {
    total_notices: number;
    latest_notice_date: string | null;
    notice_types: Record<string, number>;
  };
};

type RisksPanelProps = {
  panel: PanelMeta;
  headerColor: string;
  layoutEdit: boolean;
  reportMode?: boolean;
  reportHideSummary?: boolean;
  gazette: GazetteResponse | null;
  gazetteLoading: boolean;
  gazetteError: string | null;
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export function RisksPanel({
  panel,
  headerColor,
  layoutEdit,
  reportMode = false,
  reportHideSummary = false,
  gazette,
  gazetteLoading,
  gazetteError,
}: RisksPanelProps) {
  const notices = gazette?.notices ?? [];
  const summary = gazette?.summary ?? null;

  return (
    <PanelShell panel={panel} headerColor={headerColor} layoutEdit={layoutEdit} reportMode={reportMode}>
      <div className={`${reportMode ? "mt-2" : "mt-3"} flex flex-col gap-3 text-[11px] text-muted-foreground`}>
        {!reportHideSummary && (
          <div className="grid grid-cols-2 gap-3 text-[11px] text-muted-foreground">
            <div>
              <span className="uppercase text-[10px] tracking-wide text-muted-foreground">Total notices</span>
              <div className="text-sm font-semibold text-foreground" suppressHydrationWarning={reportMode}>
                {gazetteLoading ? "Loading…" : summary?.total_notices ?? "—"}
              </div>
            </div>
            <div>
              <span className="uppercase text-[10px] tracking-wide text-muted-foreground">Latest notice</span>
              <div className="text-sm font-semibold text-foreground" suppressHydrationWarning={reportMode}>
                {gazetteLoading ? "Loading…" : formatDate(summary?.latest_notice_date ?? null)}
              </div>
            </div>
          </div>
        )}

        {gazetteError ? (
          <div className="text-xs text-amber-600 dark:text-amber-400">{gazetteError}</div>
        ) : notices.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">
            {gazetteLoading ? "Loading gazette notices…" : "No gazette notices available."}
          </div>
        ) : (
          <div className={`rounded-md border border-border bg-card ${reportMode ? "report-avoid-break" : "overflow-auto"}`}>
            <table className={`w-full text-[11px] ${reportMode ? "report-table" : ""}`}>
              <thead className={reportMode ? "bg-card" : "sticky top-0 bg-card"}>
                <tr className="text-left text-[10px] uppercase tracking-wide" style={{ color: headerColor }}>
                  <th className="px-2 py-1">Type</th>
                  <th className="px-2 py-1">Code</th>
                  <th className="px-2 py-1">Gazette date</th>
                  <th className="px-2 py-1">Last modified</th>
                </tr>
              </thead>
              <tbody className="text-foreground">
                {notices.map((notice) => (
                  <tr key={notice.Hash_Key} className="border-t border-border/60">
                    <td className="px-2 py-1 text-muted-foreground">{notice.Notice_Type || "—"}</td>
                    <td className="px-2 py-1">{notice.Notice_Code || "—"}</td>
                    <td className="px-2 py-1">{formatDate(notice.Gazette_Date)}</td>
                    <td className="px-2 py-1">{formatDate(notice.Last_Modified_Date ?? null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PanelShell>
  );
}
